/**
 * Sweep Control (R27) — the admin surface. Mounted inside Settings' admin gate
 * (which mirrors the backend requireAdmin: role admin | super_admin), so it is
 * never an entry point for non-admins. Owns the manual-refresh-only health query
 * and hands its refetch to both the dispatched state and the log's Refresh
 * button.
 */

import { useCallback, useEffect, useState } from "react";
import { useSweepHealth } from "../hooks";
import type { RawSweepRun } from "../types";
import { HealthLog } from "./HealthLog";
import { RunSweep } from "./RunSweep";

export function SweepControl(): JSX.Element {
  const health = useSweepHealth();
  const { refetch } = health;
  const [watchedRunId, setWatchedRunId] = useState<string | null>(null);
  const [completedRun, setCompletedRun] = useState<RawSweepRun | null>(null);
  const refreshHealth = useCallback(() => {
    void refetch();
  }, [refetch]);

  const watchRun = useCallback((runId: string) => {
    setCompletedRun(null);
    setWatchedRunId(runId);
  }, []);

  useEffect(() => {
    if (!watchedRunId) return;
    const run = health.data?.runs.find((candidate) => candidate.sweepRunId === watchedRunId);
    if (!run?.finishedAt) return;
    setCompletedRun(run);
    setWatchedRunId(null);
  }, [health.data, watchedRunId]);

  useEffect(() => {
    if (!watchedRunId) return;
    void refetch();
    const interval = window.setInterval(() => void refetch(), 5_000);
    return () => window.clearInterval(interval);
  }, [refetch, watchedRunId]);

  return (
    <details className="settings-disclosure sxsw" aria-label="Sweep control">
      <summary className="settings-disclosure__summary">
        <span>
          <strong>Sweep control</strong>
          <small>Run a manual source sweep and inspect recent health.</small>
        </span>
        <span className="settings-disclosure__summary-meta">
          {watchedRunId ? "Running" : completedRun ? "Finished" : "On demand"}
          <span className="settings-disclosure__chevron" aria-hidden="true">
            ⌄
          </span>
        </span>
      </summary>

      <div className="settings-disclosure__body sxsw-body">
        <div className="sxsw-head">
          <p className="sxsw-sub">
            Scheduled sweeps run automatically. Use this control only when you intentionally want an
            extra firm-wide run.
          </p>
        </div>

        {watchedRunId && (
          <div className="sxsw-progress" role="status" aria-live="polite">
            <span className="sxsw-progress-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span>
              <strong>Sweep is running.</strong> This page is checking for completion.
            </span>
          </div>
        )}

        {completedRun && (
          <div className="sxsw-progress sxsw-progress--complete" role="status">
            <span>
              <strong>Sweep finished.</strong> {completedRun.ingestedCount} ingested ·{" "}
              {completedRun.status.replaceAll("_", " ")}
            </span>
            <button
              type="button"
              className="sx-btn sx-btn-p3"
              onClick={() => setCompletedRun(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        <RunSweep
          onRefreshHealth={refreshHealth}
          onWatchRun={watchRun}
          disclosure={health.data?.sweepDisclosure}
          disclosurePending={health.isPending}
          disclosureError={health.isError}
        />

        <HealthLog
          health={health.data}
          isPending={health.isPending}
          isError={health.isError}
          error={health.error}
          isFetching={health.isFetching}
          onRefresh={refreshHealth}
        />
      </div>
    </details>
  );
}
